import {
  PAYMENT_BATCH_STATUS_LABELS,
  PAYMENT_SOURCE_TYPE_LABELS,
  PAYMENT_SOURCE_TYPES,
  type PaymentBatch,
  type PaymentCandidate,
  type PaymentSourceType,
  paymentBatchStatusSchema,
} from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  DataTable,
  type DataTableColumn,
  DateInput,
  Dialog,
  EmptyState,
  FormField,
  Select,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  useToast,
} from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Send, Wallet } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape } from "../../../features/accounting/company.js";
import { Amount, todayIstanbul } from "../../../features/accounting/shared.js";
import { CounterpartyIdCombobox } from "../../../features/counterparties/counterparty-combobox.js";
import { ExpensesPage } from "../../../features/expenses/shared.js";
import {
  BatchStatusBadge,
  usePaymentBatches,
  usePaymentCandidates,
  usePaymentPermissions,
  useRefreshPayments,
} from "../../../features/payments/shared.js";
import {
  candidateKey,
  pruneSelection,
  selectAll,
  selectionTotal,
  toBatchLines,
  toggleSelection,
  unselectableReason,
} from "../../../features/payments/selection.js";
import { useBankAccounts } from "../../../features/treasury/definitions.js";
import { api } from "../../../lib/api.js";
import { useCursorPages } from "../../../lib/cursor-pages.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatDate } from "../../../lib/format.js";

const date = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined);
const searchSchema = z.object({
  ...companySearchShape,
  tab: z.enum(["candidates", "batches"]).optional().catch(undefined),
  dueTo: date,
  types: z.string().max(200).optional().catch(undefined),
  counterpartyId: z.string().uuid().optional().catch(undefined),
  status: paymentBatchStatusSchema.optional().catch(undefined),
});
type PaymentSearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/_app/odemeler/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: PaymentsPage,
});

function PaymentsPage(): ReactElement {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const tab = search.tab ?? "candidates";
  return (
    <ExpensesPage
      title={t("menu.payments")}
      description={t("payments.description")}
      breadcrumb={[{ label: t("menu.payments") }]}
    >
      {({ companyId }) => (
        <Tabs
          value={tab}
          onValueChange={(next) =>
            void navigate({
              search: (previous) => ({ ...previous, tab: next as "candidates" | "batches" }),
              replace: true,
            })
          }
        >
          <TabsList aria-label={t("payments.tabs")}>
            <TabsTrigger value="candidates">{t("payments.candidates.title")}</TabsTrigger>
            <TabsTrigger value="batches">{t("payments.batches.title")}</TabsTrigger>
          </TabsList>
          <TabsContent value="candidates">
            {tab === "candidates" ? <Candidates companyId={companyId} search={search} /> : null}
          </TabsContent>
          <TabsContent value="batches">
            {tab === "batches" ? <Batches companyId={companyId} search={search} /> : null}
          </TabsContent>
        </Tabs>
      )}
    </ExpensesPage>
  );
}

function Candidates({ companyId, search }: { companyId: string; search: PaymentSearch }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: Route.fullPath });
  const permissions = usePaymentPermissions();
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [creating, setCreating] = useState(false);
  const [addingToBatch, setAddingToBatch] = useState(false);

  const list = usePaymentCandidates(companyId, {
    ...(search.dueTo ? { dueTo: search.dueTo } : {}),
    ...(search.types ? { types: search.types } : {}),
    ...(search.counterpartyId ? { counterpartyId: search.counterpartyId } : {}),
  });
  const rows = list.data?.items ?? [];
  const setFilters = (patch: Partial<PaymentSearch>) => {
    // Süzgeç değişince görünmeyen satırların seçimi bırakılır.
    setSelected((current) => pruneSelection(rows, current));
    void navigate({
      search: (previous) =>
        Object.fromEntries(Object.entries({ ...previous, ...patch }).filter(([, value]) => value)),
      replace: true,
    });
  };

  const selectedTypes = new Set((search.types ?? "").split(",").filter(Boolean));
  const total = selectionTotal(rows, selected);

  const columns = useMemo<DataTableColumn<PaymentCandidate>[]>(
    () => [
      {
        id: "select",
        header: t("payments.candidates.select"),
        hideable: false,
        cell: (row) => {
          const reason = unselectableReason(row);
          return (
            <label className="flex items-center gap-2">
              <Checkbox
                checked={selected.has(candidateKey(row))}
                disabled={reason !== null}
                aria-label={t("payments.candidates.selectRow", { description: row.description })}
                onChange={() => setSelected((current) => toggleSelection(current, candidateKey(row)))}
              />
              {reason === "IN_ACTIVE_BATCH" ? (
                <StatusBadge tone="warning">
                  {t("payments.candidates.inBatch", { batchNo: row.activeBatchNo ?? "" })}
                </StatusBadge>
              ) : reason === "AMOUNT_UNKNOWN" ? (
                <StatusBadge tone="neutral">{t("payments.candidates.amountUnknown")}</StatusBadge>
              ) : null}
            </label>
          );
        },
      },
      {
        id: "sourceType",
        header: t("payments.fields.sourceType"),
        cell: (row) => <StatusBadge tone="neutral">{PAYMENT_SOURCE_TYPE_LABELS[row.sourceType]}</StatusBadge>,
        exportValue: (row) => PAYMENT_SOURCE_TYPE_LABELS[row.sourceType],
      },
      {
        id: "description",
        header: t("payments.fields.description"),
        hideable: false,
        cell: (row) => row.description,
      },
      {
        id: "counterparty",
        header: t("payments.fields.counterparty"),
        cell: (row) => row.counterpartyName ?? "—",
      },
      { id: "dueDate", header: t("payments.fields.dueDate"), cell: (row) => formatDate(row.dueDate) },
      {
        id: "overdueDays",
        header: t("payments.fields.overdueDays"),
        align: "right",
        cell: (row) =>
          row.overdueDays > 0 ? (
            <StatusBadge tone="danger">{t("payments.fields.overdue", { days: row.overdueDays })}</StatusBadge>
          ) : (
            "—"
          ),
        exportValue: (row) => row.overdueDays,
      },
      {
        id: "remainingAmount",
        header: t("payments.fields.remainingAmount"),
        align: "right",
        cell: (row) => (row.remainingAmount ? <Amount value={row.remainingAmount} /> : "—"),
        exportValue: (row) => row.remainingAmount,
      },
    ],
    [t, selected],
  );

  return (
    <div className="grid gap-4">
      <DataTable
        caption={t("payments.candidates.title")}
        columns={columns}
        rows={rows}
        rowKey={(row) => candidateKey(row)}
        loading={list.isPending}
        error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<Wallet />}
            title={t("payments.candidates.emptyTitle")}
            description={t("payments.candidates.emptyDescription")}
          />
        }
        toolbar={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setSelected(selectAll(rows))}>
              {t("payments.candidates.selectAll")}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setSelected(new Set())}>
              {t("payments.candidates.clearSelection")}
            </Button>
            <span className="text-sm">
              {t("payments.candidates.selectedCount", { count: selected.size })} · <Amount value={total} />
            </span>
            {permissions.canManage ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={selected.size === 0}
                  onClick={() => setAddingToBatch(true)}
                >
                  {t("payments.candidates.addToBatch")}
                </Button>
                <Button size="sm" disabled={selected.size === 0} onClick={() => setCreating(true)}>
                  <Send aria-hidden="true" />
                  {t("payments.candidates.createBatch")}
                </Button>
              </>
            ) : null}
          </div>
        }
        filters={
          <>
            <FormField label={t("payments.fields.dueTo")} className="w-44">
              {(control) => (
                <DateInput
                  {...control}
                  value={search.dueTo ?? null}
                  onValueChange={(value) => setFilters({ dueTo: value ?? undefined })}
                />
              )}
            </FormField>
            <fieldset className="grid gap-1">
              <legend className="text-sm font-medium">{t("payments.fields.sourceTypes")}</legend>
              <div className="flex flex-wrap gap-3">
                {PAYMENT_SOURCE_TYPES.map((type) => (
                  <label key={type} className="flex items-center gap-1.5 text-sm">
                    <Checkbox
                      checked={selectedTypes.has(type)}
                      onChange={(event) => {
                        const next = new Set(selectedTypes);
                        if (event.target.checked) next.add(type);
                        else next.delete(type);
                        setFilters({ types: next.size > 0 ? [...next].join(",") : undefined });
                      }}
                    />
                    {PAYMENT_SOURCE_TYPE_LABELS[type as PaymentSourceType]}
                  </label>
                ))}
              </div>
            </fieldset>
            <FormField label={t("payments.fields.counterparty")} className="w-64">
              {(control) => (
                <CounterpartyIdCombobox
                  {...control}
                  companyId={companyId}
                  counterpartyId={search.counterpartyId ?? null}
                  onChange={(id) => setFilters({ counterpartyId: id ?? undefined })}
                />
              )}
            </FormField>
          </>
        }
      />

      <CreateBatchDialog
        open={creating}
        companyId={companyId}
        candidates={rows}
        selected={selected}
        onOpenChange={setCreating}
        onCreated={() => setSelected(new Set())}
      />

      <AddToBatchDialog
        open={addingToBatch}
        companyId={companyId}
        candidates={rows}
        selected={selected}
        onOpenChange={setAddingToBatch}
        onAdded={() => setSelected(new Set())}
      />
    </div>
  );
}

/**
 * Seçili satırları MEVCUT bir taslak partiye ekler. Kaynak başka etkin partide bekliyorsa sunucu
 * `PAYMENT_SOURCE_LOCKED` döner (çift ödeme engeli) — ileti kullanıcıya aynen gösterilir.
 */
function AddToBatchDialog({
  open,
  companyId,
  candidates,
  selected,
  onOpenChange,
  onAdded,
}: {
  open: boolean;
  companyId: string;
  candidates: readonly PaymentCandidate[];
  selected: ReadonlySet<string>;
  onOpenChange: (open: boolean) => void;
  onAdded: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshPayments();
  const drafts = usePaymentBatches(companyId, { status: "DRAFT" });
  const [batchId, setBatchId] = useState("");
  const [error, setError] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: async () => {
      const batch = (drafts.data?.items ?? []).find((item) => item.id === batchId)!;
      return api.POST("/payment-batches/{id}/lines", {
        params: { path: { id: batchId } },
        body: { lines: toBatchLines(candidates, selected), version: batch.version },
      });
    },
    onSuccess: async () => {
      onOpenChange(false);
      onAdded();
      await refresh();
      toast({ tone: "success", title: t("payments.candidates.addedToBatch") });
    },
    onError: (caught) => setError(errorMessage(caught)),
  });

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("payments.candidates.addToBatch")}
      description={t("payments.candidates.addToBatchDescription", { count: selected.size })}
    >
      <form
        className="grid gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          setError(null);
          add.mutate();
        }}
      >
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <FormField label={t("payments.candidates.draftBatch")} required>
          {(control) => (
            <Select
              {...control}
              required
              value={batchId}
              onChange={(event) => setBatchId(event.target.value)}
            >
              <option value="">{t("common.select")}</option>
              {(drafts.data?.items ?? []).map((batch) => (
                <option key={batch.id} value={batch.id}>
                  {batch.batchNo} · {formatDate(batch.paymentDate)}
                </option>
              ))}
            </Select>
          )}
        </FormField>
        {(drafts.data?.items ?? []).length === 0 ? (
          <Alert tone="info">{t("payments.candidates.noDraftBatch")}</Alert>
        ) : null}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" loading={add.isPending} disabled={batchId === ""}>
            {t("payments.candidates.addToBatch")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function CreateBatchDialog({
  open,
  companyId,
  candidates,
  selected,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  companyId: string;
  candidates: readonly PaymentCandidate[];
  selected: ReadonlySet<string>;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const refresh = useRefreshPayments();
  const bankAccounts = useBankAccounts(companyId);
  const [paymentDate, setPaymentDate] = useState<string | null>(todayIstanbul());
  const [bankAccountId, setBankAccountId] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async () =>
      (
        await api.POST("/payment-batches", {
          body: {
            companyId,
            paymentDate: paymentDate ?? "",
            bankAccountId,
            description: description.trim() || null,
            lines: toBatchLines(candidates, selected),
          },
        })
      ).data!,
    onSuccess: async (batch) => {
      onOpenChange(false);
      onCreated();
      await refresh();
      toast({ tone: "success", title: t("payments.batches.created", { batchNo: batch.batchNo }) });
      void navigate({ to: "/odemeler/$id", params: { id: batch.id } });
    },
    onError: (caught) => setError(errorMessage(caught)),
  });

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("payments.candidates.createBatch")}
      description={t("payments.candidates.createBatchDescription", { count: selected.size })}
    >
      <form
        className="grid gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          setError(null);
          create.mutate();
        }}
      >
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <FormField label={t("payments.fields.paymentDate")} required>
          {(control) => <DateInput {...control} value={paymentDate} onValueChange={setPaymentDate} />}
        </FormField>
        <FormField label={t("payments.fields.bankAccount")} required>
          {(control) => (
            <Select
              {...control}
              required
              value={bankAccountId}
              onChange={(event) => setBankAccountId(event.target.value)}
            >
              <option value="">{t("common.select")}</option>
              {(bankAccounts.data ?? []).map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name}
                </option>
              ))}
            </Select>
          )}
        </FormField>
        <FormField label={t("payments.fields.description")}>
          {(control) => (
            <Textarea
              {...control}
              value={description}
              maxLength={500}
              onChange={(event) => setDescription(event.target.value)}
            />
          )}
        </FormField>
        <Card className="bg-muted/40 p-3 text-sm">
          {t("payments.candidates.selectedCount", { count: selected.size })} ·{" "}
          <Amount value={selectionTotal(candidates, selected)} />
        </Card>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" loading={create.isPending} disabled={bankAccountId === ""}>
            {t("payments.candidates.createBatch")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function Batches({ companyId, search }: { companyId: string; search: PaymentSearch }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: Route.fullPath });
  const filters = search.status ? { status: search.status } : {};
  const pages = useCursorPages(JSON.stringify(filters));
  const list = usePaymentBatches(companyId, filters, pages.cursor);

  const columns = useMemo<DataTableColumn<Omit<PaymentBatch, "lines">>[]>(
    () => [
      {
        id: "batchNo",
        header: t("payments.fields.batchNo"),
        hideable: false,
        cell: (row) => (
          <Link
            to="/odemeler/$id"
            params={{ id: row.id }}
            className="font-medium underline underline-offset-4"
          >
            {row.batchNo}
          </Link>
        ),
        exportValue: (row) => row.batchNo,
      },
      {
        id: "paymentDate",
        header: t("payments.fields.paymentDate"),
        cell: (row) => formatDate(row.paymentDate),
      },
      {
        id: "bankAccount",
        header: t("payments.fields.bankAccount"),
        cell: (row) => row.bankAccountName ?? "—",
      },
      {
        id: "lineCount",
        header: t("payments.fields.lineCount"),
        align: "center",
        cell: (row) => String(row.lineCount),
      },
      {
        id: "totalAmount",
        header: t("payments.fields.totalAmount"),
        align: "right",
        cell: (row) => <Amount value={row.totalAmount} />,
        exportValue: (row) => row.totalAmount,
      },
      {
        id: "status",
        header: t("common.status"),
        hideable: false,
        cell: (row) => <BatchStatusBadge status={row.status} />,
        exportValue: (row) => PAYMENT_BATCH_STATUS_LABELS[row.status],
      },
    ],
    [t],
  );

  return (
    <DataTable
      caption={t("payments.batches.title")}
      columns={columns}
      rows={list.data?.items ?? []}
      rowKey={(row) => row.id}
      loading={list.isPending}
      error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
      emptyState={
        <EmptyState
          headingLevel={2}
          icon={<Send />}
          title={t("payments.batches.emptyTitle")}
          description={t("payments.batches.emptyDescription")}
        />
      }
      filters={
        <FormField label={t("common.status")} className="w-44">
          {(control) => (
            <Select
              {...control}
              value={search.status ?? ""}
              onChange={(event) =>
                void navigate({
                  search: (previous) => ({
                    ...previous,
                    status:
                      event.target.value === ""
                        ? undefined
                        : paymentBatchStatusSchema.parse(event.target.value),
                  }),
                  replace: true,
                })
              }
            >
              <option value="">{t("common.all")}</option>
              {paymentBatchStatusSchema.options.map((status) => (
                <option key={status} value={status}>
                  {PAYMENT_BATCH_STATUS_LABELS[status]}
                </option>
              ))}
            </Select>
          )}
        </FormField>
      }
      pagination={{
        hasPrevious: pages.hasPrevious,
        hasNext: list.data?.page.hasMore ?? false,
        onPrevious: pages.previous,
        onNext: () => pages.next(list.data?.page.nextCursor),
        pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
      }}
    />
  );
}
